import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useParentBasketContext } from '../../../../context/ParentBasketProvider';
import { useParentDashboardContextContext } from '../../../../context/ParentDashboardContext';
import { useParentOrderContextContext } from '../../../../context/ParentOrderContext';
import { useThemeContext } from '../../../../context/ThemeProvider';
import CustomBadge from '../../../CustomBadge/CustomBadge';
import CustomChakraButton from '../../../CustomChakraButton/CustomChakraButton';
import CustomHorizontalTabs from '../../../CustomHorizontalTabs/CustomHorizontalTabs';
import WideDateInput from '../../../Forms/WideDateInput/WideDateInput';
import calculatePrice from '../../../../utils/calculators/calculatePrice';
import { PurchasableItemInterface } from '../../../../shared/type/parentOrderContext.type';
import { useSafeTranslation } from '../../../../hooks/useSafeTranslation';
import { useAlertContext } from '../../../../context/AlertProvider';
import { useWaitingScreen, WaitingScreen } from '../../WaitingScreen/WaitingScreen';
import BadgeWithMessage from '../../../BadgeWithMessage/BadgeWithMessage';

const OrderOnDays = ({ navigationSlot }: { navigationSlot?: React.ReactNode }) => {
  const { t } = useSafeTranslation();
  const navigate = useNavigate();
  const todayDate = new Date();
  const { isMobile } = useThemeContext();
  const { setBasketStatus, basketStatus } = useParentDashboardContextContext();
  const { addItemToBasket, placeOrderForPayment, isEmpty } = useParentBasketContext();
  const {
    parentOrderContext,
    setParentOrderContextDate,
    tenantId,
    setSkipFetchingParentOrderContext
  } = useParentOrderContextContext();
  const [selectedConsumerId, setSelectedUserId] = useState<number>();
  const { setAlertProperties, setShow: setShowAlert } = useAlertContext();
  const enforcedDaysSelectionForConsumer =
    parentOrderContext?.consumerOrderContexts.find(
      (consumer) => consumer.consumerId === selectedConsumerId
    )?.daySelection || [];
  const [isNotFullSizeScreen] = useMediaQuery('(max-width: 1024px)');
  const [shouldHideBasket] = useMediaQuery('(max-width: 1200px)');
  const { isOpen: showWaitingScreen, openWaitingScreen, closeWaitingScreen } = useWaitingScreen();
  const containerStylesForStakesSelectorBar = !isNotFullSizeScreen
    ? 'hide'
    : 'w-full bottom-0 border-b border-b-amber-400 flex-col gap-4';

  const [date, setDate] = useState<{
    year: number;
    month: number;
    day: number;
  }>({
    year: todayDate.getFullYear(),
    month: todayDate.getMonth() + 1,
    day: todayDate.getDate()
  });
  const selectedDate = moment(`${date.year}-${date.month}-${date.day}`, 'YYYY-M-D');
  const selectedGroup = enforcedDaysSelectionForConsumer.find((group) =>
    group.some((d) => moment(d).isSame(selectedDate, 'day'))
  );
  const orderSectionMaxWidth =
    !isMobile && basketStatus.show ? 'max-w-[calc(100vw-700px)]' : 'max-w-full';

  useEffect(() => {
    setParentOrderContextDate({
      year: date.year,
      month: date.month
    });
  }, [date.month]);

  useEffect(() => {
    if (!isMobile) {
      setBasketStatus((prev) => ({
        ...prev,
        show: true
      }));
    }

    if (shouldHideBasket) {
      setBasketStatus((prev) => ({
        ...prev,
        show: false
      }));
    }
  }, [isMobile, shouldHideBasket]);

  const formatDateForComparison = (year: number, month: number, day: number) => {
    const formattedMonth = String(month).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');
    return `${year}-${formattedMonth}-${formattedDay}`;
  };

  const goToOrderSummary = async () => {
    openWaitingScreen();

    placeOrderForPayment()
      .then(() => {
        closeWaitingScreen();
        navigate('/parent/order/summary');
      })
      .catch((e) => {
        closeWaitingScreen();
        setAlertProperties({
          timeout: 3000,
          title: e.message,
          status: 'error'
        });
        setShowAlert(true);
      });
  };

  const orderDays = useMemo(
    () =>
      parentOrderContext?.consumerOrderContexts.find(
        (context) => context.consumerId === selectedConsumerId
      )?.orderDays,
    [parentOrderContext, selectedConsumerId]
  );

  const selectedOrder = useMemo(
    () =>
      parentOrderContext?.consumerOrderContexts
        .find((context) => context.consumerId === selectedConsumerId)
        ?.orderDays?.find(
          (item) => item.when === formatDateForComparison(date.year, date.month, date.day)
        ),
    [parentOrderContext, selectedConsumerId, date]
  );

  const onAddToCartClick = (itemToAdd: PurchasableItemInterface) => {
    const whenDates = selectedGroup?.length
      ? selectedGroup
      : [formatDateForComparison(date.year, date.month, date.day)];
    const withoutDaysWhenStakeIsNotAvailable = whenDates.filter(
      (date) =>
        !!orderDays
          ?.find((day) => day.when === date)
          ?.purchasableItems?.some((item) => item.stakeId === itemToAdd.stakeId)
    );

    const orderedItems = withoutDaysWhenStakeIsNotAvailable.map((when) => {
      const orderForDay = orderDays?.find((day) => day.when === when);
      const itemForDay = orderForDay?.purchasableItems?.find(
        (item) => item.stakeId === itemToAdd.stakeId
      );

      return {
        purchasableItem: {
          name: itemForDay?.name ?? itemToAdd.name,
          meals: itemForDay?.meals ?? itemToAdd.meals,
          price: itemForDay?.price ?? itemToAdd.price,
          originalPrice: itemForDay?.originalPrice ?? itemToAdd.originalPrice,
          stakeId: itemToAdd.stakeId,
          alreadyBoughtCount: itemForDay?.alreadyBoughtCount ?? itemToAdd.alreadyBoughtCount,
          orderCount: itemForDay?.orderCount ?? itemToAdd.orderCount,
          stakeLimitLeft: itemForDay?.stakeLimitLeft ?? itemToAdd.stakeLimitLeft,
          itemPurchaseLimit: itemForDay?.itemPurchaseLimit ?? itemToAdd.itemPurchaseLimit
        },
        count: 1,
        connectedDates: selectedGroup ? selectedGroup : [],
        when
      };
    });

    addItemToBasket({
      tenantId: tenantId || 0,
      consumerId: selectedConsumerId || 0,
      orderedItems,
      useCredit: false
    });

    setAlertProperties({
      timeout: 3000,
      title: t('parent_order.added_to_cart'),
      status: 'success'
    });
    setShowAlert(true);
  };

  useEffect(() => {
    const minDate = parentOrderContext?.minOrderDate;
    const maxDate = parentOrderContext?.maxOrderDate;

    if (minDate && maxDate) {
      const today = new Date();
      if (today < new Date(minDate)) {
        setDate({
          year: new Date(minDate).getFullYear(),
          month: new Date(minDate).getMonth() + 1,
          day: new Date(minDate).getDate()
        });
      } else if (today > new Date(maxDate)) {
        setDate({
          year: new Date(maxDate).getFullYear(),
          month: new Date(maxDate).getMonth() + 1,
          day: new Date(maxDate).getDate()
        });
      }
    }
  }, [parentOrderContext?.minOrderDate, parentOrderContext?.maxOrderDate]);

  useEffect(() => {
    setSkipFetchingParentOrderContext(false);

    return () => {
      setSkipFetchingParentOrderContext(true);
    };
  }, []);

  useEffect(() => {
    if (parentOrderContext) {
      setSelectedUserId(parentOrderContext.consumerOrderContexts[0].consumerId);
    }
  }, [parentOrderContext]);

  return (
    <section className="OrderOnDays pb-72 relative w-full flex bg-grayLight-50">
      <div
        className={`basis-full ${orderSectionMaxWidth} flex flex-col gap-spacing-5xl pt-spacing-md lg:px-spacing-3xl pb-spacing-3xl`}>
        <div className="flex flex-col gap-spacing-lg">
          {navigationSlot && navigationSlot}

          <div className="flex flex-col px-spacing-xl lg:px-spacing-0 gap-spacing-lg">
            <p className="font-semibold text-2xl text-grayLight-900">
              {t('parent_order.day_order.title')}
            </p>
            <p className="font-normal text-grayLight-600" style={{ fontSize: '16px' }}>
              {t('parent_order.day_order.subtitle')}
            </p>
          </div>
        </div>
        <div className="w-full">
          <CustomHorizontalTabs
            value={selectedConsumerId}
            onChange={(value) => {
              setSelectedUserId(value);
            }}
            data={
              parentOrderContext
                ? parentOrderContext.consumerOrderContexts.map((consumer) => {
                    return {
                      label: isMobile
                        ? `${consumer.firstName}`
                        : `${consumer.firstName} ${consumer.lastName}`,
                      value: consumer.consumerId
                    };
                  })
                : []
            }
          />
        </div>
        <div className="flex flex-col pt-spacing-4xl lg:pt-0 gap-spacing-4xl h-full">
          <WideDateInput
            setDate={setDate}
            maxDate={parentOrderContext?.maxOrderDate}
            minDate={parentOrderContext?.minOrderDate}
          />
          <div className="flex flex-col gap-spacing-lg overflow-y-auto">
            {selectedOrder ? (
              <>
                {selectedOrder.purchasableItems && selectedOrder.purchasableItems.length > 0 ? (
                  selectedOrder.purchasableItems.map((item) => (
                    <div
                      key={item.stakeId}
                      className="flex bg-white flex-col rounded-xl p-spacing-xl gap-spacing-sm shadow-[0px_4px_6px_-2px_#10182808,0px_12px_16px_-4px_#10182814]">
                      {item.alreadyBoughtCount > 0 && (
                        <div>
                          <CustomBadge size="sm" color="blue">
                            {t('parent_order.day_order.already_purchased_alert')} (x
                            {item.alreadyBoughtCount})
                          </CustomBadge>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <div>
                          <p className="text-grayLight-900 text-sm md:text-lg font-semibold">
                            {item.name}
                          </p>
                        </div>
                        <div className="flex flex-col gap-spacing-md items-end justify-between">
                          <div className="flex gap-spacing-xs">
                            {item.originalPrice === item.price || (
                              <p className="text-grayLight-500 text-base md:text-xl font-medium line-through">
                                {calculatePrice(item.originalPrice, null)}
                              </p>
                            )}
                            <p className="text-grayLight-900 text-base md:text-xl font-semibold">
                              {calculatePrice(item.price, null)}
                            </p>
                          </div>
                          <div className="flex flex-col items-end">
                            <div>
                              <CustomChakraButton
                                buttonProps={{
                                  onClick: () => onAddToCartClick(item),
                                  disabled:
                                    (item.itemPurchaseLimit !== null &&
                                      item.itemPurchaseLimit <= 0) ||
                                    (item.alreadyBoughtCount === item.itemPurchaseLimit &&
                                      item.itemPurchaseLimit !== null)
                                }}
                                size={isNotFullSizeScreen ? 'sm' : 'md'}
                                hierarchy="secondaryColor"
                                iconPosition="onlyText">
                                {t('parent_order.day_order.add_to_cart')}
                              </CustomChakraButton>
                            </div>
                            {item.itemPurchaseLimit !== null && item.itemPurchaseLimit <= 0 && (
                              <p className="text-sm font-normal text-error-600">
                                {t('parent_order.day_order.stake_limit_alert')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-spacing-md">
                        {item.meals.map((meal) => (
                          <div key={meal.courseName} className="flex flex-col gap-spacing-xs">
                            <div className="border-b box-border border-brand-300">
                              <p className="text-grayLight-500 text-xs md:text-sm font-normal">
                                {meal.courseName}
                              </p>
                            </div>
                            <div className="flex gap-spacing-md">
                              <div>
                                <p className="text-grayLight-600 font-semibold text-xs md:text-base">
                                  {meal.mealDto?.name}
                                </p>
                              </div>
                              <div className="flex gap-spacing-md">
                                {meal.mealDto?.allergens.map((allergen) => (
                                  <BadgeWithMessage
                                    key={`AllergenKey-${allergen.id}`}
                                    size="small"
                                    tooltipLabel={
                                      <p className="rounded-lg pt-spacing-md pr-spacing-lg pb-spacing-md pl-spacing-lg text-sm text-grayLight-900 font-medium">
                                        {allergen.name}
                                      </p>
                                    }>{`${allergen.formalNumber}`}</BadgeWithMessage>
                                ))}
                              </div>
                            </div>
                            <div>
                              <p className="text-grayLight-600 font-normal text-xs md:text-sm">
                                {meal.mealDto?.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="m-auto">
                    {t('parent_order.day_order.not_allowed_order')} <br />
                    {t('parent_order.day_order.reason')}: <b>{selectedOrder.comment}</b>
                  </p>
                )}
              </>
            ) : (
              <p className="m-auto">{t('parent_order.day_order.not_allowed_order')}</p>
            )}
          </div>
        </div>
      </div>
      <div
        className={`${containerStylesForStakesSelectorBar} fixed left-0 flex items-center gap-4 px-6 pt-6 pb-8 bg-white border-t border-t-amber-400 z-40`}>
        {isNotFullSizeScreen && (
          <CustomChakraButton
            buttonProps={{
              onClick: () => setBasketStatus((prev) => ({ ...prev, show: !prev.show })),
              disabled: isEmpty,
              className: 'grow'
            }}
            size="sm"
            hierarchy="primary"
            fullWidth
            iconPosition="onlyText">
            {t('basket.title')}
          </CustomChakraButton>
        )}

        {isNotFullSizeScreen && (
          <CustomChakraButton
            buttonProps={{
              onClick: goToOrderSummary,
              disabled: isEmpty || showWaitingScreen,
              className: 'grow'
            }}
            size="sm"
            hierarchy="primary"
            fullWidth
            iconPosition="onlyText">
            {t('basket.to_summary')}
          </CustomChakraButton>
        )}
      </div>
      <WaitingScreen
        isOpen={showWaitingScreen}
        onClose={() => {
          return navigate('/parent/order/summary');
        }}
      />
    </section>
  );
};

export default OrderOnDays;
